/**
 * Hybrid resolution of clinic filter conditions.
 *
 * For every blueprint from `GET /clinics/available-conditions`:
 *   1. if an EXISTING plugin is registered whose `filterConditionId` equals the
 *      blueprint `id` (and its value-kind matches) → reuse that registration
 *      (keeps the polished bespoke UIs: code-spectrum, fab-key, geo, …);
 *   2. otherwise → synthesize a GENERIC plugin from the blueprint.
 *
 * The match key is the condition id (`filterConditionId`), NOT the plugin
 * `type` — the two deliberately differ (e.g. type `administrativeDistrictIds`
 * vs. id `administrativeDistrictId`; the API sends the id).
 */
import type { BdCondition } from '@bindoc-projects/condition-domain-lib';
import type { FilterConditionPluginSpecification } from '../filter.plugin';
import type {
  FilterConditionComponentLoader,
  FilterRegistrationToken,
} from '../token';
import type {
  ClinicConditionBlueprint,
  ClinicConditionType,
} from './blueprint.model';
import { createGenericSpecification } from './generic-plugins';

export type ResolvedSource = 'existing' | 'generic';

export interface ResolvedClinicCondition {
  blueprint: ClinicConditionBlueprint;
  specification: FilterConditionPluginSpecification<unknown>;
  /**
   * Existing registration's UI component loader, or `null` for the generic
   * branch (which supplies its own component via `createGenericRegistrations`).
   */
  component: FilterConditionComponentLoader | null;
  source: ResolvedSource;
}

export interface UnsupportedBlueprint {
  blueprint: ClinicConditionBlueprint;
  reason: string;
}

/** An existing registration whose value-kind disagreed with the blueprint. */
export interface GuardFallback {
  blueprint: ClinicConditionBlueprint;
  expected: ClinicConditionType;
  detected: ClinicConditionType;
}

export interface ClinicConditionResolution {
  resolved: ResolvedClinicCondition[];
  unsupported: UnsupportedBlueprint[];
  guardFallbacks: GuardFallback[];
}

/** OPERATOR blueprints (`and`/`or`) are tree combinators, not selectable leaves. */
const OPERATOR_TYPE: ClinicConditionType = 'OPERATOR';

const LEAF_KINDS: ReadonlySet<ClinicConditionType> = new Set<ClinicConditionType>([
  'LIST',
  'RANGE',
  'FLAG',
]);

/**
 * Registration tokens for the generic conditions of a resolution, suitable for
 * REHYDRATION (`FilterConditionUtil.fromConditionJson`) — no UI component, just
 * the id + spec so a stored condition can be reconstructed. The data layer
 * merges these with the DI `FILTER_REGISTRATION` before loading saved filters.
 */
export function genericRegistrationTokens(
  resolution: ClinicConditionResolution,
): FilterRegistrationToken[] {
  return resolution.resolved
    .filter((it) => it.source === 'generic')
    .map((it) => ({
      filterType: it.blueprint.id,
      filterConditionId: it.blueprint.id,
      filterConditionPluginSpecification: it.specification,
      filterConditionComponent: null,
    }));
}

export function resolveClinicConditions(
  blueprints: ClinicConditionBlueprint[],
  registrations: FilterRegistrationToken[],
): ClinicConditionResolution {
  const byConditionId = new Map<string, FilterRegistrationToken>();
  for (const token of registrations) {
    // Index truthy ids only: several plugins carry a null id and would
    // otherwise collide on the null key and false-match a blueprint.
    if (token.filterConditionId) {
      byConditionId.set(token.filterConditionId, token);
    }
  }

  const resolution: ClinicConditionResolution = {
    resolved: [],
    unsupported: [],
    guardFallbacks: [],
  };

  blueprints.forEach((blueprint, index) => {
    if (blueprint.type === OPERATOR_TYPE) return;

    const existing = byConditionId.get(blueprint.id);
    if (existing) {
      const detected = detectValueKind(existing.filterConditionPluginSpecification);
      if (!isValueKindMismatch(blueprint.type, detected)) {
        resolution.resolved.push({
          blueprint,
          specification: existing.filterConditionPluginSpecification,
          component: existing.filterConditionComponent,
          source: 'existing',
        });
        return;
      }
      // Confident mismatch → record and fall through to the generic branch.
      resolution.guardFallbacks.push({
        blueprint,
        expected: blueprint.type,
        detected: detected as ClinicConditionType,
      });
    }

    const genericSpec = createGenericSpecification(blueprint, index);
    if (genericSpec) {
      resolution.resolved.push({
        blueprint,
        specification: genericSpec as FilterConditionPluginSpecification<unknown>,
        component: null,
        source: 'generic',
      });
    } else {
      resolution.unsupported.push({
        blueprint,
        reason: `value-kind ${blueprint.type} not handled by the generic branch yet`,
      });
    }
  });

  return resolution;
}

/**
 * Only a CONFIDENT disagreement between two known leaf kinds counts as a
 * mismatch. Undetermined detection (e.g. a bespoke/composite plugin) trusts the
 * existing registration.
 */
function isValueKindMismatch(
  expected: ClinicConditionType,
  detected: ClinicConditionType | null,
): boolean {
  if (detected === null) return false;
  if (!LEAF_KINDS.has(expected) || !LEAF_KINDS.has(detected)) return false;
  return expected !== detected;
}

/**
 * Best-effort detection of an existing plugin's value-kind by instantiating it
 * and inspecting the inner object of its serialized condition. Returns `null`
 * when it cannot be determined.
 */
function detectValueKind(
  specification: FilterConditionPluginSpecification<unknown>,
): ClinicConditionType | null {
  try {
    const condition = specification.newInstance().toCondition();
    if (!condition) return null;
    return detectValueKindFromCondition(condition);
  } catch {
    return null;
  }
}

function detectValueKindFromCondition(
  condition: BdCondition,
): ClinicConditionType | null {
  const raw = condition.toJSON() as Record<string, unknown>;
  const inner = Object.values(raw)[0];
  if (!inner || typeof inner !== 'object') return null;
  const keys = Object.keys(inner as Record<string, unknown>);
  if (keys.includes('includes') || keys.includes('excludes')) return 'LIST';
  if (keys.includes('from') || keys.includes('to')) return 'RANGE';
  if (keys.includes('flag')) return 'FLAG';
  if (keys.includes('equals')) return 'EQUALS';
  return null;
}
